import React from 'react';
import { Job } from '../../types/jobs';
import { format, parseISO, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import { MapPin, Calendar, DollarSign, CheckCircle, Globe, ArrowUpRight, Building2, Clock, ExternalLink, Award, Tag } from 'lucide-react';
import * as amplitude from '@amplitude/analytics-browser';

interface JobCardProps {
  job: Job;
  isPremium?: boolean;
  onClick: (job: Job) => void;
  categoryMap?: Record<string, string>;
}

export function JobCard({ job, isPremium = false, onClick, categoryMap = {} }: JobCardProps) {
  // Format publication date with improved error handling
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Non spécifiée';
    
    try {
      // First check if the string is a valid date format
      const parsedDate = parseISO(dateString);
      if (!isValid(parsedDate)) {
        return 'Non spécifiée';
      }
      return format(parsedDate, 'dd MMMM yyyy', { locale: fr });
    } catch (e) {
      console.error('Date formatting error:', e);
      return 'Non spécifiée';
    }
  };

  // Function to safely decode HTML
  const decodeHTML = (html?: string) => {
    if (!html) return '';
    try {
      const txt = document.createElement('textarea');
      txt.innerHTML = html;
      return txt.value;
    } catch (e) {
      console.error('HTML decode error:', e);
      return html || '';
    }
  };

  // Get employer display name
  const employerDisplay = React.useMemo(() => {
    if (typeof job.employer === 'object' && job.employer !== null && !Array.isArray(job.employer)) {
      // Handle the case where employer is an object with a name property
      return job.employer.name || "Employeur non spécifié";
    } else if (Array.isArray(job.employer) && job.employer.length > 0) {
      // Handle the case where employer is an array with content
      if (typeof job.employer[0] === 'string') {
        return job.employer[0];
      } else if (typeof job.employer[0] === 'object' && job.employer[0]?.name) {
        return job.employer[0].name;
      }
    } else if (typeof job.employer === 'string' && job.employer) {
      // Handle the case where employer is a string
      return job.employer;
    }
    
    // Fallback for empty arrays or null/undefined employers
    return "Employeur non spécifié";
  }, [job.employer]);

  // Get category name if available
  const categoryName = React.useMemo(() => {
    if (!job.category) return null;
    
    // Convert category to string for proper lookup
    const categoryId = String(job.category);
    return categoryMap[categoryId] || `Catégorie ${job.category}`;
  }, [job.category, categoryMap]);

  const handleCardClick = () => {
    // Track click before navigation
    amplitude.track('Job Card Click', {
      jobId: job.id,
      jobTitle: job.title || 'Unknown job',
      isPremium: isPremium
    });
    
    // Call the onClick handler passed from parent
    if (typeof onClick === 'function') {
      onClick(job);
    }
  };

  return (
    <div
      onClick={handleCardClick}
      className="group relative rounded-2xl border flex flex-col bg-white dark:bg-gray-800 border-gray-200/60 dark:border-gray-700/50 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden cursor-pointer"
    >
      {/* Premium indicator */}
      {isPremium && (
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-400 via-orange-400 to-red-400"></div>
      )}

      {/* Verified indicator */}
      {!isPremium && job.employerVerified && (
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400"></div>
      )}
      
      <div className="p-6 flex-1 flex flex-col">
        {/* Badges row */}
        <div className="flex flex-wrap gap-2 mb-4">
          {isPremium && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full text-xs font-semibold shadow-sm">
              <Award className="w-3.5 h-3.5" />
              <span>Premium</span>
            </span>
          )}
          {job.employerVerified && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-full text-xs font-semibold shadow-sm">
              <CheckCircle className="w-3.5 h-3.5" />
              <span>Vérifié</span>
            </span>
          )}
          {job.locationFlexible === 1 && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-full text-xs font-semibold shadow-sm">
              <Globe className="w-3.5 h-3.5" />
              <span>Flexible</span>
            </span>
          )}
        </div>

        {/* Job Title */}
        <h3 className="text-lg sm:text-xl font-bold line-clamp-2 text-gray-900 dark:text-white mb-4">
          {decodeHTML(job.title)}
        </h3>
        
        {/* Job Details */}
        <div className="flex flex-col gap-2.5 mb-5 flex-1">
          {/* Employer - Only show if employer is specified */}
          {employerDisplay && employerDisplay !== "Employeur non spécifié" && (
            <div className="flex items-center gap-3">
              <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="font-medium text-sm text-gray-700 dark:text-gray-300 line-clamp-1">
                {employerDisplay}
              </span>
            </div>
          )}

          {/* Category */}
          {categoryName && (
            <div className="flex items-center gap-3">
              <Tag className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="line-clamp-1 text-sm text-gray-600 dark:text-gray-400">
                {categoryName}
              </span>
            </div>
          )}

          {/* Location */}
          <div className="flex items-center gap-3">
            <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="line-clamp-1 text-sm text-gray-600 dark:text-gray-400">
              {decodeHTML(job.location)}
            </span>
          </div>

          {/* Salary */}
          <div className="flex items-center gap-3">
            <DollarSign className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 line-clamp-1">
              {job.salaryFull || 'À négocier'}
            </span>
          </div>

          {/* Start Date */}
          <div className="flex items-center gap-3">
            <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="text-sm text-gray-600 dark:text-gray-400 line-clamp-1">
              {job.startASAP
                ? 'Dès que possible'
                : job.startDate
                  ? `Début: ${formatDate(job.startDate)}`
                  : 'Date de début non précisée'}
            </span>
          </div>
        </div>

        {/* Footer with Publication Date & Apply Button */}
        <div className="flex items-center gap-3 justify-between pt-4 mt-auto border-t border-gray-100 dark:border-gray-700/50">
          <div className="flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {formatDate(job.published)}
            </span>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCardClick();
            }}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded-xl text-sm font-semibold transition-all shadow-sm hover:shadow-md transform hover:scale-105"
          >
            <span>Détails</span>
            <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}